import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageActions from '../components/PageActions';
import HomeVideo from '../components/HomeVideo';

const HomePage = () => {
  useEffect(() => {
    if (window.scrollY !== 0) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, []);

  // Promise sections data - restructured per PPT
  const promises = [
    {
      number: 1,
      title: 'Your Country Cabin',
      description: <>Nestled in the Dorset countryside, this cosy cabin is a warm, <strong className="font-semibold">private retreat on a working farm - where comfort meets the outdoors</strong>. It has a covered terrace, a well-equipped kitchen-living area, a unique bathroom and two bedrooms. It's the place to unwind, disconnect and enjoy nature.</>,
      image: 'https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1771402938/DSC_7890_mfwgdj.jpg',
      link: '/your-country-cabin',
      bgColor: 'bg-[#f5f1e8]'
    },
    {
      number: 2,
      title: 'Your Landscape',
      description: <><strong className="font-semibold">Immerse yourself in Dorset's natural beauty, where rolling countryside meets the sea.</strong> Your cabin sits on a working farm - animals nearby, slower days, and a small private kitchen garden for your use. The quiet here is made for unwinding and reflection.</>,
      image: 'https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1771402354/DSC_8018_vtsuxp.jpg',
      link: '/your-landscape',
      bgColor: 'bg-[#e8e2d8]'
    },
    {
      number: 3,
      title: 'Your Host',
      description: <><strong className="font-semibold">Your farmer-host offers a quiet, personal welcome.</strong> Always nearby but never intrusive, they can help with local tips, practical support, or anything you need to make stays at your country cabin carefree and tranquil.</>,
      image: 'https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1771402341/foto_tractor_qeasse.jpg',
      link: '/your-host',
      bgColor: 'bg-[#f5f1e8]'
    }
  ];

  return (
    <div className="bg-[#f5f1e8]">
      <SEO 
        title="Your Setl - Your place in nature | Luxury Country Cabins in Dorset"
        description="Privately owned country cabins in Dorset. Reconnect with nature, embrace slowness, and find balance on a working farm along the Jurassic Coast."
        keywords="Dorset country cabins, luxury tiny houses Dorset, nature retreat UK, digital detox Dorset, countryside accommodation, farm stay Dorset, Jurassic Coast accommodation, eco cabins UK, slow living retreat, Dorset glamping"
        ogImage="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1779198166/Doctored_image_nnslm2.png"
        url="https://www.yoursetl.co.uk"
      />
      <PageActions />
      
      {/* Hero Section — Static brochure image */}
      <section className="relative h-screen flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1779198166/Doctored_image_nnslm2.png"
            alt="Your Setl country cabin at sunset in Dorset"
            className="w-full h-full object-cover"
            style={{ imageRendering: 'auto' }}
            loading="eager"
            fetchPriority="high"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent z-10" />

        <div className="relative z-20 px-8 md:px-16 pb-16 md:pb-20 max-w-3xl">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#f5f1e8] leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Your place in nature
          </h1>
          <div className="w-16 h-px bg-[#f5f1e8]/70 my-5" />
          <p
            className="text-base md:text-lg font-light text-[#f5f1e8] tracking-wide"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Privately owned country cabins in Dorset
          </p>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-20">
          <div className="w-6 h-10 border-2 border-[#f5f1e8]/60 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-[#f5f1e8]/60 rounded-full animate-scroll" />
          </div>
        </div>
      </section>

      {/* Decorative Divider - After Hero */}
      <div className="bg-[#f5f1e8] py-8">
        <div className="max-w-4xl mx-auto text-center px-6">
          <div className="flex items-center justify-center gap-4">
            <div className="h-px bg-[#d4a574] flex-1 max-w-[100px]"></div>
            <h2 
              className="text-2xl md:text-3xl font-light text-[#3a2f28] tracking-wide"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Your Setl
            </h2>
            <div className="h-px bg-[#d4a574] flex-1 max-w-[100px]"></div>
          </div>
          <p 
            className="text-[#3a2f28]/70 mt-3 text-sm md:text-base"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Your place in nature
          </p>
        </div>
      </div>

      {/* Video Section */}
      <section className="py-12 lg:py-16 px-6 bg-[#f5f1e8]">
        <div className="max-w-4xl mx-auto">
          <HomeVideo />
        </div>
      </section>

      {/* Section 1: Your Setl - Embrace Slowness (Image LEFT on desktop) */}
      <section className="py-16 lg:py-20 px-6 bg-[#f5f1e8]">
        <div className="max-w-6xl mx-auto">
          {/* Mobile: Title first */}
          <div className="lg:hidden mb-6">
            <h2
              className="text-2xl md:text-3xl font-light tracking-wide text-[#d4a574] mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Your Setl
            </h2>
            <h3
              className="text-2xl md:text-3xl font-light tracking-wide text-[#3a2f28]"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Embrace Slowness
            </h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1782115859/yousetl-dorset-roadmarks_aaledw.png"
                alt="Your Setl - Embrace Slowness"
                className="w-full h-auto"
              />
            </div>
            
            <div>
              <div className="hidden lg:block">
                <h2
                  className="text-3xl md:text-4xl font-light tracking-wide text-[#d4a574] mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Your Setl
                </h2>
                <h3
                  className="text-3xl md:text-4xl font-light tracking-wide text-[#3a2f28] mb-6"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Embrace Slowness
                </h3>
              </div>
              <p
                className="text-base lg:text-lg leading-relaxed text-[#3a2f28]/85 mb-4 lg:mb-6"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Life moves fast. Here, we slow it down. In the middle of the noise and rush, we created something rare: space to breathe. Escape when you need to.
              </p>
              <p
                className="text-sm lg:text-base leading-relaxed text-[#3a2f28]/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Your cabin is a quiet place where time slows, simplicity returns, and the things that matter come back into focus. Here in Dorset, nature is your compass. Your cabin, your host, and the land work together to rebalance life, turning screens to silence and busy to calm.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Your Setl - Uniquely Designed Concept (Image RIGHT on desktop) */}
      <section className="py-16 lg:py-20 px-6 bg-[#e8e2d8]">
        <div className="max-w-6xl mx-auto">
          {/* Mobile: Title first */}
          <div className="lg:hidden mb-6">
            <h2
              className="text-2xl md:text-3xl font-light tracking-wide text-[#d4a574] mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Your Setl
            </h2>
            <h3
              className="text-2xl md:text-3xl font-light tracking-wide text-[#3a2f28]"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Uniquely Designed Concept
            </h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Image - Shows after title on mobile, RIGHT on desktop */}
            <div className="relative h-[280px] md:h-[350px] lg:h-[500px] rounded-lg overflow-hidden shadow-2xl lg:order-2">
              <img
                src="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1771402943/DSC_8352_b1azs1.jpg"
                alt="Your Setl - Uniquely Designed Concept"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Text - LEFT on desktop */}
            <div className="lg:order-1">
              {/* Desktop: Title */}
              <div className="hidden lg:block">
                <h2
                  className="text-3xl md:text-4xl font-light tracking-wide text-[#d4a574] mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Your Setl
                </h2>
                <h3
                  className="text-3xl md:text-4xl font-light tracking-wide text-[#3a2f28] mb-6"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Uniquely Designed Concept
                </h3>
              </div>
              <p
                className="text-base lg:text-lg leading-relaxed text-[#3a2f28]/85"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                A unique concept designed to help you slow down, unwind, and reconnect. Being the owner of your country cabin set in peaceful nature and hosted with care, is your escape back to balance. This is our promise and commitment to you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Decorative Divider - Our Promises */}
      <div className="bg-[#f5f1e8] py-8">
        <div className="max-w-4xl mx-auto text-center px-6">
          <div className="flex items-center justify-center gap-4">
            <div className="h-px bg-[#d4a574] flex-1 max-w-[100px]"></div>
            <h2 
              className="text-2xl md:text-3xl font-light text-[#3a2f28] tracking-wide"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Our Promises
            </h2>
            <div className="h-px bg-[#d4a574] flex-1 max-w-[100px]"></div>
          </div>
          <p 
            className="text-[#3a2f28]/70 mt-3 text-sm md:text-base"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Five commitments that make Your Setl truly exceptional
          </p>
        </div>
      </div>

      {/* Promise Sections 1-3 */}
      {promises.map((promise, index) => {
        // Alternating pattern for desktop
        const imageOnLeft = index % 2 === 0;
        return (
          <section
            key={promise.title}
            className={`py-16 lg:py-20 px-6 ${promise.bgColor}`}
          >
            <div className="max-w-7xl mx-auto">
              {/* Mobile: Title always first */}
              <div className="lg:hidden mb-6">
                <h2
                  className="text-xl md:text-2xl font-light tracking-wide text-[#d4a574] mb-1"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Your Setl
                  <span className="text-[#3a2f28]/60 mx-2">|</span>
                  <span className="text-[#3a2f28]/80">Promise {promise.number}</span>
                </h2>
                <h3
                  className="text-2xl md:text-3xl font-light tracking-wide text-[#3a2f28] mt-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {promise.title}
                </h3>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {/* Image - Second on mobile, alternates on desktop */}
                <div className={`relative h-[280px] md:h-[350px] lg:h-[500px] rounded-lg overflow-hidden shadow-2xl ${imageOnLeft ? 'lg:order-1' : 'lg:order-2'}`}>
                  <img
                    src={promise.image}
                    alt={promise.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                
                {/* Text - Third on mobile */}
                <div className={imageOnLeft ? 'lg:order-2' : 'lg:order-1'}>
                  {/* Desktop: Title */}
                  <div className="hidden lg:block">
                    <h2
                      className="text-3xl md:text-4xl font-light tracking-wide text-[#d4a574] mb-1"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      Your Setl
                      <span className="text-[#3a2f28]/60 mx-3">|</span>
                      <span className="text-[#3a2f28]/80">Promise {promise.number}</span>
                    </h2>
                    <h3
                      className="text-3xl md:text-4xl font-light tracking-wide text-[#3a2f28] mb-6 mt-2"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {promise.title}
                    </h3>
                  </div>
                  <p
                    className="text-base lg:text-lg leading-relaxed text-[#3a2f28]/85 mb-6 lg:mb-8"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {promise.description}
                  </p>
                  <Link
                    to={promise.link}
                    className="inline-block px-6 lg:px-8 py-3 bg-[#3a2f28] text-[#f5f1e8] font-light tracking-wide hover:bg-[#d4a574] transition-all duration-300 rounded-sm text-sm lg:text-base"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Explore More
                  </Link>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Promise 4 - Farm-fresh Food & Relax Section */}
      <section className="py-16 lg:py-20 px-6 bg-[#e8e2d8]">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-xl md:text-2xl lg:text-3xl font-light tracking-wide text-center mb-2"
          >
            <span style={{ fontFamily: 'Playfair Display, serif' }} className="text-[#d4a574]">Your Setl</span>
            <span className="text-[#3a2f28]/60 mx-2 lg:mx-3">|</span>
            <span className="text-[#3a2f28]/80" style={{ fontFamily: 'Playfair Display, serif' }}>Promise 4</span>
          </h2>
          
          <h3
            className="text-2xl md:text-3xl lg:text-4xl font-light tracking-wide text-[#3a2f28] mb-3 lg:mb-4 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Farm-fresh Food
          </h3>
          <p
            className="text-base lg:text-lg text-[#3a2f28]/70 mb-8 lg:mb-12 text-center max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Relax and enjoy
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="relative group">
              <div className="relative h-[300px] md:h-[350px] rounded-lg overflow-hidden shadow-2xl mb-6">
                <img
                  src="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1771402357/DSC_8401_mcfek3.jpg"
                  alt="Farm-fresh Food"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <p
                className="text-base leading-relaxed text-[#3a2f28]/85 text-center mb-4"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Farm-fresh ingredients, honest meals, and the taste of authenticity. Every bite connects you to the land.
              </p>
              <div className="text-center">
                <Link
                  to="/your-host"
                  className="inline-block text-[#d4a574] font-light tracking-wide hover:text-[#3a2f28] transition-colors duration-300 underline underline-offset-4"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Explore More
                </Link>
              </div>
            </div>

            <div className="relative group">
              <div className="relative h-[300px] md:h-[350px] rounded-lg overflow-hidden shadow-2xl mb-6">
                <img
                  src="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1771402356/DSC_8292_dvnm8v.jpg"
                  alt="Relax & Enjoy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <p
                className="text-base leading-relaxed text-[#3a2f28]/85 text-center mb-4"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                No schedules, no notifications. Just the rustling of leaves, the warmth of the fire, and time that's truly yours.
              </p>
              <div className="text-center">
                <Link
                  to="/your-host"
                  className="inline-block text-[#d4a574] font-light tracking-wide hover:text-[#3a2f28] transition-colors duration-300 underline underline-offset-4"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Explore More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promise 5 - Exclusively Limited in Scale (Image RIGHT on desktop) */}
      <section className="py-16 lg:py-20 px-6 bg-[#f5f1e8]">
        <div className="max-w-6xl mx-auto">
          <div className="lg:hidden mb-6">
            <h2
              className="text-xl md:text-2xl font-light tracking-wide text-[#d4a574] mb-1"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Your Setl
              <span className="text-[#3a2f28]/60 mx-2">|</span>
              <span className="text-[#3a2f28]/80">Promise 5</span>
            </h2>
            <h3
              className="text-2xl md:text-3xl font-light tracking-wide text-[#3a2f28] mt-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Exclusively Limited in Scale
            </h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="relative h-[280px] md:h-[350px] lg:h-[500px] rounded-lg overflow-hidden shadow-2xl lg:order-2">
              <img
                src="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1771402938/DSC_7915_erxdvk.jpg"
                alt="Exclusively Limited in Scale"
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="lg:order-1">
              <div className="hidden lg:block">
                <h2
                  className="text-3xl md:text-4xl font-light tracking-wide text-[#d4a574] mb-1"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Your Setl
                  <span className="text-[#3a2f28]/60 mx-3">|</span>
                  <span className="text-[#3a2f28]/80">Promise 5</span>
                </h2>
                <h3
                  className="text-3xl md:text-4xl font-light tracking-wide text-[#3a2f28] mb-6 mt-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Exclusively Limited in Scale
                </h3>
              </div>
              <p
                className="text-base lg:text-lg leading-relaxed text-[#3a2f28]/85"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Created to offer true rest, the concept is intentionally small in scale: <strong className="font-semibold">just eight country cabins set within more than three acres of beautiful Dorset countryside</strong>. No crowds, no rush. Simply a place designed for presence, not numbers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-[#3a2f28]">
        <div className="max-w-3xl mx-auto text-center">
          <h2
            className="text-3xl md:text-4xl font-light tracking-wide text-[#f5f1e8] mb-8"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Ready to Find Your Balance?
          </h2>
          <p
            className="text-lg leading-relaxed text-[#f5f1e8]/90 mb-10"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Let's explore how nature can bring calm back into your life.
          </p>
          <Link
            to="/contact"
            className="inline-block px-10 py-4 bg-[#f5f1e8] text-[#3a2f28] text-lg font-light tracking-wide hover:bg-[#d4a574] hover:text-[#f5f1e8] transition-all duration-300 rounded-sm shadow-lg hover:shadow-xl"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
